import { Alert, Button, Col, Form, Row, Select, Spin, Tag } from "antd";
import { MinusCircleOutlined, PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { apiErrorMessage } from "../../api/apiError";
import React, { useEffect, useState } from "react";
import { ApiService, WorkflowRole } from "../../api";
import { OptionProps } from "antd/es/select";
import { openNotificationWithIcon } from "../../constants";

const formItemLayout = {
  labelCol: {
    xs: { span: 8 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 18 },
    sm: { span: 18 },
  },
};

const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 18, offset: 8 },
    sm: { span: 18, offset: 8 },
  },
};

interface Props {
  requestId?: number;
  workflowId?: number;
  onSuccess: (result: any) => void;
  duplicateRequestId?: number | undefined;
  setDuplicateRequestId?: (duplicateRequestId: number | undefined) => void;
  showPrev?: boolean;
  onPrev?: () => void;
}

/** Slots nobody picks for.
 *
 *  The two `Auto*` pool modes resolve from a group; the two derived modes
 *  resolve from the org chart. Both must render read-only — a derived slot in
 *  the manual branch would put a *required* person picker in front of the
 *  requester for a slot the server fills itself, and the form could then not
 *  be submitted at all. */
const isAutomatic = (role: WorkflowRole) =>
  role.assignment === "AutoQuorum" ||
  role.assignment === "AutoAll" ||
  role.assignment === "AutoManager" ||
  role.assignment === "AutoUnitHead";

const isDerived = (role: WorkflowRole) =>
  role.assignment === "AutoManager" || role.assignment === "AutoUnitHead";

/**
 * Approver picker for one approval stage.
 *
 * Two shapes, decided by whether the stage defines named roles:
 *  - roles present  -> one labelled row per slot ("Permit Issuer": <select>),
 *    with the search restricted to the slot's pool where one is configured, and
 *    automatic slots shown read-only. Submits `assignments`.
 *  - no roles       -> the original anonymous add/remove list. Submits `users`.
 *
 * The legacy branch is kept because most templates have no roles configured, and
 * the backend still accepts (and requires) the flat payload for those stages.
 */
const WorkflowViewPage = ({
  requestId,
  workflowId,
  onSuccess,
  duplicateRequestId,
  setDuplicateRequestId,
  showPrev,
  onPrev,
}: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [users, setUsers] = useState<OptionProps[]>([]);
  // Options are per-slot when roles are in play: each slot may search a
  // different pool, so one shared list would leak one pool's members into
  // another slot's dropdown.
  const [roleUsers, setRoleUsers] = useState<Record<number, OptionProps[]>>({});
  const [roles, setRoles] = useState<WorkflowRole[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [initializing, setInitializing] = useState<boolean>(false);

  const hasRoles = roles.length > 0;
  const manualRoles = roles.filter((r) => !isAutomatic(r));

  useEffect(() => {
    (async () => {
      if (workflowId === undefined || workflowId === null) return;
      try {
        setRoles(await ApiService.apiMainWorkflowRolesList(workflowId));
      } catch (e) {
        // A stage whose roles cannot be loaded falls back to the legacy picker
        // rather than blocking the wizard.
        console.log(e);
        setRoles([]);
      }
    })();
  }, [workflowId]);

  useEffect(() => {
    (async () => {
      if (
        requestId !== null &&
        requestId !== undefined &&
        duplicateRequestId === undefined
      ) {
        await getApprovers(requestId);
      }
    })();
  }, [requestId, workflowId, roles.length]);

  const getApprovers = async (requestId?: number) => {
    setInitializing(true);
    try {
      const approvers = await ApiService.apiMainRequestApproversGetApproversList(
        requestId!.toString(),
        workflowId!
      );

      const userInfos = approvers.map((x) => ({
        value: x.user_id,
        label: x.user_fullname,
        children: null,
      }));

      if (roles.length > 0) {
        // Seed each slot's dropdown with its saved value so the name renders
        // before the user searches anything.
        const perRole: Record<number, OptionProps[]> = {};
        const values: Record<string, any> = {};
        approvers.forEach((x, i) => {
          if (x.role_id === null || x.role_id === undefined) return;
          perRole[x.role_id] = [userInfos[i]];
          values[`role_${x.role_id}`] = x.user_id;
        });
        setRoleUsers(perRole);
        form.setFieldsValue(values);
      } else {
        setUsers(userInfos);
        form.setFieldsValue({ users: approvers.map((x) => x.user_id) });
      }
    } catch (e) {
      console.log(e);
    }
    setInitializing(false);
  };

  useEffect(() => {
    (async () => {
      await getApprovers(duplicateRequestId);
    })();
  }, [duplicateRequestId]);

  const handleUserSearch = async (value: string) => {
    const result = (await ApiService.apiAccountsUsersList(value)).map((x) => ({
      value: x.id,
      label: x.name,
      children: null,
    }));
    setUsers(result);
  };

  const handleRoleUserSearch = async (role: WorkflowRole, value: string) => {
    // `pool` is null for ManualAny, which makes this the unrestricted search.
    const result = (await ApiService.apiAccountsUsersList(value, role.pool)).map(
      (x) => ({ value: x.id, label: x.name, children: null })
    );
    setRoleUsers((prev) => ({ ...prev, [role.id]: result }));
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const payload = hasRoles
        ? {
            request_id: requestId!,
            workflow_id: workflowId!,
            assignments: manualRoles
              .filter((role) => values[`role_${role.id}`])
              .map((role) => ({
                user_id: values[`role_${role.id}`],
                role_id: role.id,
              })),
          }
        : {
            request_id: requestId!,
            workflow_id: workflowId!,
            users: values.users,
          };

      const result =
        requestId !== null && requestId !== undefined
          ? await ApiService.apiMainRequestApproversUpdate(0, payload)
          : await ApiService.apiMainRequestApproversCreate(payload);

      onSuccess(result);
      openNotificationWithIcon("success", t("Form successfully saved"));
      await getApprovers(requestId);
    } catch (e: any) {
      // Pool-membership and duplicate-slot errors come back as 400s with a
      // message worth showing rather than swallowing. The hand-rolled chain
      // that used to be here missed `body.message` -- which is the shape this
      // codebase's own ValidationErrors use -- and fell through to the
      // generated client's `e.message`, so a refusal the server had written in
      // plain English reached the approver as the word "Bad Request".
      openNotificationWithIcon(
        "error",
        apiErrorMessage(e, t("Could not save approvers"))
      );
    }
    setLoading(false);
  };

  const renderRoleRows = () => (
    <>
      {roles.map((role) =>
        isAutomatic(role) ? (
          <Form.Item {...formItemLayout} label={role.title} key={role.id}>
            <Alert
              type="info"
              showIcon
              icon={<TeamOutlined />}
              style={{ width: "60%" }}
              message={
                isDerived(role)
                  ? /* The server's own sentence (main/assignment.describe), so
                       the reason this slot has no picker is the same wording
                       the designer showed the administrator who set it up. */
                    role.resolver_text ||
                    t("Worked out from the organisation chart when this step opens")
                  : role.assignment === "AutoAll"
                    ? t("Every member of {{group}} must approve", {
                        group: role.pool_name ?? "",
                      })
                    : t("Any {{count}} member(s) of {{group}} must approve", {
                        count: role.quorum,
                        group: role.pool_name ?? "",
                      })
              }
            />
          </Form.Item>
        ) : (
          /* The group tag is in `extra`, NOT a second child of Form.Item.
             antd binds `value`/`onChange` into its child only when there is
             exactly one; with a Select *and* a Tag inside, the Select stayed
             uncontrolled, nothing ever reached the form store, and the
             required rule failed however many times you picked a person —
             "Please input user's name or delete this field" on a field you
             had just filled in. Only slots drawn from a pool render the tag,
             so this went unnoticed until a template used ManualFromPool. */
          <Form.Item
            {...formItemLayout}
            label={role.title}
            key={role.id}
            name={`role_${role.id}`}
            validateTrigger={["onChange", "onBlur"]}
            extra={
              role.pool_name ? (
                <Tag color="blue" style={{ marginTop: 6 }}>
                  {t("from {{group}}", { group: role.pool_name })}
                </Tag>
              ) : null
            }
            rules={[
              {
                required: true,
                message: t("Please input user's name or delete this field."),
              },
            ]}
          >
            <Select
              showSearch
              disabled={loading}
              optionLabelProp="label"
              filterOption={false}
              style={{ width: "60%" }}
              onSearch={(value) => handleRoleUserSearch(role, value)}
              options={roleUsers[role.id] ?? []}
              notFoundContent={loading ? <Spin size="small" /> : null}
              placeholder={t("Type name..")}
            />
          </Form.Item>
        )
      )}
    </>
  );

  const renderLegacyList = () => (
    <Form.List
      name="users"
      rules={[
        {
          validator: async (_, names) => {
            if (!names || names.length < 1) {
              return Promise.reject(new Error(t("At least 1 approver")));
            }
          },
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => (
        <>
          {fields.map((field) => (
            <Form.Item
              {...formItemLayout}
              label={t("Approver")}
              required={false}
              key={field.key}
            >
              <Form.Item
                {...field}
                validateTrigger={["onChange", "onBlur"]}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t("Please input user's name or delete this field."),
                  },
                ]}
                noStyle
              >
                <Select
                  showSearch
                  disabled={loading}
                  optionLabelProp="label"
                  filterOption={false}
                  style={{ width: "60%" }}
                  onSearch={handleUserSearch}
                  options={users}
                  notFoundContent={loading ? <Spin size="small" /> : null}
                  placeholder={t("Type name..")}
                ></Select>
              </Form.Item>
              {fields.length > 1 ? (
                <MinusCircleOutlined
                  style={{ marginLeft: 10 }}
                  className="dynamic-delete-button"
                  disabled={loading}
                  onClick={() => remove(field.name)}
                />
              ) : null}
            </Form.Item>
          ))}
          <Form.Item {...formItemLayoutWithOutLabel}>
            <Button
              type="dashed"
              style={{ width: "60%" }}
              onClick={() => add()}
              icon={<PlusOutlined />}
              disabled={loading}
            >
              {t("Add field")}
            </Button>
            <Form.ErrorList errors={errors} />
          </Form.Item>
        </>
      )}
    </Form.List>
  );

  return (
    <Spin spinning={initializing}>
      <Form
        form={form}
        onFinish={onFinish}
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 16 }}
      >
        {hasRoles ? renderRoleRows() : renderLegacyList()}
        <Row justify={showPrev ? "space-between" : "end"} align="middle">
          {showPrev && (
            <Col>
              <Button onClick={onPrev}>{t("Previous")}</Button>
            </Col>
          )}
          <Col>
            <Button
              style={{ minWidth: 100 }}
              type="primary"
              loading={loading}
              htmlType="submit"
              // Every slot is automatic: nothing to submit.
              disabled={hasRoles && manualRoles.length === 0}
            >
              {t("Save")}
            </Button>
          </Col>
        </Row>
      </Form>
    </Spin>
  );
};

export default WorkflowViewPage;
